"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import { ReferralSchedulingRecommendation } from "@/src/components/comms/referral-scheduling-recommendation";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import ErrorAlert from "@/src/components/error-alert";
import { AthenaReferralCards } from "@/src/components/tasks/athena-referral-cards";
import { StediEligibilitySummary } from "@/src/components/tasks/stedi-eligibility-summary";
import { SyncIncompleteActions } from "@/src/components/tasks/sync-incomplete-actions";
import { useProvidersForDiagnostics } from "@/src/lib/data-fetchers/diagnosis-providers-fetchers";
import { useLocationProvidersInstructions } from "@/src/lib/data-fetchers/location-providers-fetchers";
import { useStediEligibilityCheck } from "@/src/lib/data-fetchers/stedi-eligibility-check-fetchers";
import { logger } from "@/src/lib/logging/logger";
import { tryGetEhrSystemId } from "@/src/lib/resolve-ehr-ids";
import { getFormattedAddress } from "@/src/models/address";
import { Appointment } from "@/src/models/appointment";
import { Coverage } from "@/src/models/coverage";
import { Referral } from "@/src/models/documents/referral";
import { EHRDocument } from "@/src/models/ehr-document";
import { Patient } from "@/src/models/patient";
import { getPersonName } from "@/src/models/person";
import { getPractitionerNameWithCredentials, Practitioner } from "@/src/models/practitioner";
import { type LocationProvidersInstructionsEntry } from "@/src/requests/location-providers-instructions";
interface AthenaData {
  patientDetails: Patient | null;
  coverages: Coverage[];
  careTeam: Practitioner[];
  appointmentReminders: Appointment[];
  bookedAppointments: Appointment[];
  document: EHRDocument | null;
  expectedDocument: EHRDocument | null;
  documentPatientId?: string | null;
}
interface AthenaReferralInstructionsProps {
  referral: Referral;
  departmentId?: string;
  athenaData: AthenaData | null;
  customInstructions: string | null;
  showSchedulingRecommendation?: boolean;
  taskId: string;
}
type DisplayProvider = {
  text: string;
  preferred?: boolean;
};
type DisplayRow = {
  key: string;
  locationLabel: string;
  departmentName: string;
  providerGroup: string;
  providers: DisplayProvider[];
};
function buildReceivingLocationRows(params: {
  isPending: boolean;
  entries: LocationProvidersInstructionsEntry[];
  hasReceivingLocation: boolean;
  receivingLocationAddress: string;
  providersForDiagnostics: {
    name: string;
    isPreferred?: boolean;
  }[] | undefined;
}): DisplayRow[] {
  const {
    isPending,
    entries,
    hasReceivingLocation,
    receivingLocationAddress,
    providersForDiagnostics
  } = params;
  if (isPending) {
    return [];
  }
  const diagnosticProviders: DisplayProvider[] = (providersForDiagnostics ?? []).map(provider => ({
    text: provider.name,
    preferred: provider.isPreferred
  }));
  if (entries.length === 0) {
    return diagnosticProviders.length === 0 ? [] : [{
      key: "diagnosis-providers",
      locationLabel: receivingLocationAddress,
      departmentName: "N/A",
      providerGroup: "N/A",
      providers: diagnosticProviders
    }];
  }
  return entries.map(entry => ({
    key: entry.receivingLocationId,
    locationLabel: hasReceivingLocation ? receivingLocationAddress : entry.department?.name || "N/A",
    departmentName: entry.department?.name || "N/A",
    providerGroup: entry.department?.providerGroup || "N/A",
    providers: entry.providers.length > 0 ? entry.providers.map(text => ({
      text
    })) : hasReceivingLocation ? diagnosticProviders : []
  }));
}
export function AthenaReferralInstructions(t0) {
  const $ = _c(91);
  const {
    referral,
    departmentId,
    athenaData,
    customInstructions,
    showSchedulingRecommendation: t1,
    taskId
  } = t0;
  const showSchedulingRecommendation = t1 === undefined ? false : t1;
  let t2;
  let t3;
  if ($[0] !== athenaData) {
    t2 = () => {
      logger.info("Data from Athena", {
        athenaData
      });
    };
    t3 = [athenaData];
    $[0] = athenaData;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  const {
    eligibilityCheck
  } = useStediEligibilityCheck(referral.id);
  const receivingLocationId = referral.receivingLocation?.id ?? null;
  const {
    data: locationProvidersData,
    isPending
  } = useLocationProvidersInstructions(receivingLocationId);
  let t4;
  if ($[3] !== referral.diagnosisList) {
    t4 = referral.diagnosisList || [];
    $[3] = referral.diagnosisList;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    data: providersForDiagnostics
  } = useProvidersForDiagnostics(t4, departmentId);
  let T0;
  let t10;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[5] !== athenaData || $[6] !== isPending || $[7] !== locationProvidersData || $[8] !== providersForDiagnostics || $[9] !== receivingLocationId || $[10] !== referral.receivingLocation || $[11] !== referral.referringLocation || $[12] !== referral.referringProvider) {
    const receivingLocationAddress = referral.receivingLocation?.address ? getFormattedAddress(referral.receivingLocation.address) || "N/A" : "N/A";
    const receivingLocationRows = buildReceivingLocationRows({
      isPending,
      entries: locationProvidersData ?? [],
      hasReceivingLocation: receivingLocationId != null,
      receivingLocationAddress,
      providersForDiagnostics
    });
    const showCatalogBanner = receivingLocationId == null && (locationProvidersData?.length ?? 0) > 0;
    let t11;
    if ($[20] !== athenaData?.patientDetails) {
      t11 = getPersonName(athenaData?.patientDetails, true, true) || "N/A";
      $[20] = athenaData?.patientDetails;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    const patientName = t11;
    let t12;
    if ($[22] !== athenaData) {
      t12 = athenaData?.patientDetails && tryGetEhrSystemId(athenaData.patientDetails) || "N/A";
      $[22] = athenaData;
      $[23] = t12;
    } else {
      t12 = $[23];
    }
    const ehrId = t12;
    const documentId = athenaData?.document?.ehrId ?? "N/A";
    let t13;
    if ($[24] !== referral.referringProvider) {
      t13 = referral.referringProvider ? getPractitionerNameWithCredentials(referral.referringProvider) : "N/A";
      $[24] = referral.referringProvider;
      $[25] = t13;
    } else {
      t13 = $[25];
    }
    const referringProviderName = t13;
    const referringProviderNPI = referral.referringProvider?.npiNumber || "N/A";
    let t14;
    if ($[26] !== referral.referringLocation) {
      t14 = referral.referringLocation?.address ? getFormattedAddress(referral.referringLocation.address) || "N/A" : "N/A";
      $[26] = referral.referringLocation;
      $[27] = t14;
    } else {
      t14 = $[27];
    }
    const referringLocationAddress = t14;
    t9 = "space-y-8 text-foreground";
    if ($[28] !== athenaData) {
      t10 = !athenaData && <ErrorAlert compact={true} message="No Athena data available" />;
      $[28] = athenaData;
      $[29] = t10;
    } else {
      t10 = $[29];
    }
    T0 = EhrInstructionsCard;
    t5 = "text-sm text-muted-foreground";
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <h3 className="text-md font-semibold text-center mb-4">Athena Referral Instructions</h3>;
      $[30] = t6;
    } else {
      t6 = $[30];
    }
    let t15;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <span className="font-medium text-foreground">Patient Name: </span>;
      $[31] = t15;
    } else {
      t15 = $[31];
    }
    let t16;
    if ($[32] !== patientName) {
      t16 = <div>{t15}<CopyableContent text={patientName} /></div>;
      $[32] = patientName;
      $[33] = t16;
    } else {
      t16 = $[33];
    }
    let t17;
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <span className="font-medium text-foreground">EHR ID: </span>;
      $[34] = t17;
    } else {
      t17 = $[34];
    }
    let t18;
    if ($[35] !== ehrId) {
      t18 = <div>{t17}<CopyableContent text={ehrId} /></div>;
      $[35] = ehrId;
      $[36] = t18;
    } else {
      t18 = $[36];
    }
    let t19;
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <span className="font-medium text-foreground">Document ID: </span>;
      $[37] = t19;
    } else {
      t19 = $[37];
    }
    let t20;
    if ($[38] !== documentId) {
      t20 = <div>{t19}<CopyableContent text={documentId} /></div>;
      $[38] = documentId;
      $[39] = t20;
    } else {
      t20 = $[39];
    }
    let t21;
    if ($[40] !== athenaData) {
      t21 = athenaData?.documentPatientId && <div><span className="font-medium text-foreground">Document Patient ID: </span><CopyableContent text={athenaData.documentPatientId} /></div>;
      $[40] = athenaData;
      $[41] = t21;
    } else {
      t21 = $[41];
    }
    const t22 = athenaData?.documentPatientId ? "" : "col-start-2";
    let t23;
    if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = <span className="font-medium text-foreground">Referring Provider: </span>;
      $[42] = t23;
    } else {
      t23 = $[42];
    }
    let t24;
    if ($[43] !== referringProviderName) {
      t24 = <CopyableContent text={referringProviderName} />;
      $[43] = referringProviderName;
      $[44] = t24;
    } else {
      t24 = $[44];
    }
    let t25;
    if ($[45] !== t22 || $[46] !== t24) {
      t25 = <div className={t22}>{t23}{t24}</div>;
      $[45] = t22;
      $[46] = t24;
      $[47] = t25;
    } else {
      t25 = $[47];
    }
    let t26;
    if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
      t26 = <span className="font-medium text-foreground">NPI: </span>;
      $[48] = t26;
    } else {
      t26 = $[48];
    }
    let t27;
    if ($[49] !== referringProviderNPI) {
      t27 = <div>{t26}<CopyableContent text={referringProviderNPI} /></div>;
      $[49] = referringProviderNPI;
      $[50] = t27;
    } else {
      t27 = $[50];
    }
    let t28;
    if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
      t28 = <span className="font-medium text-foreground">Referring Location: </span>;
      $[51] = t28;
    } else {
      t28 = $[51];
    }
    let t29;
    if ($[52] !== referringLocationAddress) {
      t29 = <div className="col-span-3">{t28}<CopyableContent text={referringLocationAddress} /></div>;
      $[52] = referringLocationAddress;
      $[53] = t29;
    } else {
      t29 = $[53];
    }
    if ($[54] !== t16 || $[55] !== t18 || $[56] !== t20 || $[57] !== t21 || $[58] !== t25 || $[59] !== t27 || $[60] !== t29) {
      t7 = <div className="grid grid-cols-3 gap-y-4 mb-6">{t16}{t18}{t20}{t21}{t25}{t27}{t29}</div>;
      $[54] = t16;
      $[55] = t18;
      $[56] = t20;
      $[57] = t21;
      $[58] = t25;
      $[59] = t27;
      $[60] = t29;
      $[61] = t7;
    } else {
      t7 = $[61];
    }
    t8 = receivingLocationRows.length > 0 && <div className="mb-4 my-2"><h3 className="font-semibold text-foreground my-2">Receiving Location and Providers:</h3>{showCatalogBanner && <p className="text-xs text-muted-foreground mb-2">All locations — no receiving location</p>}<div className="overflow-x-auto"><table className="w-full border border-border rounded-lg"><thead className="bg-muted"><tr><th className="text-left p-3 font-medium text-foreground">Receiving Location</th><th className="text-left p-3 font-medium text-foreground">Department</th><th className="text-left p-3 font-medium text-foreground">Provider Group</th><th className="text-left p-3 font-medium text-foreground">Providers</th></tr></thead><tbody>{receivingLocationRows.map(_temp2)}</tbody></table></div></div>;
    $[5] = athenaData;
    $[6] = isPending;
    $[7] = locationProvidersData;
    $[8] = providersForDiagnostics;
    $[9] = receivingLocationId;
    $[10] = referral.receivingLocation;
    $[11] = referral.referringLocation;
    $[12] = referral.referringProvider;
    $[13] = T0;
    $[14] = t10;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    T0 = $[13];
    t10 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  let t11;
  if ($[62] !== referral || $[63] !== showSchedulingRecommendation) {
    t11 = showSchedulingRecommendation && <ReferralSchedulingRecommendation referral={referral} />;
    $[62] = referral;
    $[63] = showSchedulingRecommendation;
    $[64] = t11;
  } else {
    t11 = $[64];
  }
  let t12;
  if ($[65] !== customInstructions) {
    t12 = customInstructions && <p className="text-sm mt-4">{customInstructions}</p>;
    $[65] = customInstructions;
    $[66] = t12;
  } else {
    t12 = $[66];
  }
  let t13;
  if ($[67] !== eligibilityCheck) {
    t13 = eligibilityCheck && <StediEligibilitySummary check={eligibilityCheck} />;
    $[67] = eligibilityCheck;
    $[68] = t13;
  } else {
    t13 = $[68];
  }
  let t14;
  if ($[69] !== taskId) {
    t14 = <div className="mt-4"><SyncIncompleteActions taskId={taskId} /></div>;
    $[69] = taskId;
    $[70] = t14;
  } else {
    t14 = $[70];
  }
  let t15;
  if ($[71] !== t11 || $[72] !== t12 || $[73] !== t13 || $[74] !== t14 || $[75] !== t5 || $[76] !== t6 || $[77] !== t7 || $[78] !== t8) {
    t15 = <div className={t5}>{t6}{t7}{t8}{t11}{t12}{t13}{t14}</div>;
    $[71] = t11;
    $[72] = t12;
    $[73] = t13;
    $[74] = t14;
    $[75] = t5;
    $[76] = t6;
    $[77] = t7;
    $[78] = t8;
    $[79] = t15;
  } else {
    t15 = $[79];
  }
  let t16;
  if ($[80] !== T0 || $[81] !== t15) {
    t16 = <T0>{t15}</T0>;
    $[80] = T0;
    $[81] = t15;
    $[82] = t16;
  } else {
    t16 = $[82];
  }
  let t17;
  if ($[83] !== athenaData || $[84] !== referral) {
    t17 = <AthenaReferralCards referral={referral} athenaData={athenaData} />;
    $[83] = athenaData;
    $[84] = referral;
    $[85] = t17;
  } else {
    t17 = $[85];
  }
  let t18;
  if ($[86] !== t10 || $[87] !== t16 || $[88] !== t17 || $[89] !== t9) {
    t18 = <div className={t9}>{t10}{t16}{t17}</div>;
    $[86] = t10;
    $[87] = t16;
    $[88] = t17;
    $[89] = t9;
    $[90] = t18;
  } else {
    t18 = $[90];
  }
  return t18;
}
function _temp2(row) {
  return <tr key={row.key} className="border-t border-border"><td className="p-3 text-sm"><span className="text-muted-foreground"><CopyableContent text={row.locationLabel} /></span></td><td className="p-3 text-sm"><span className="text-muted-foreground"><CopyableContent text={row.departmentName} /></span></td><td className="p-3 text-sm"><span className="text-muted-foreground"><CopyableContent text={row.providerGroup} /></span></td><td className="p-3 text-sm"><div className="space-y-1">{row.providers.map(_temp)}</div></td></tr>;
}
function _temp(provider) {
  return <div key={provider.text}><span className={provider.preferred ? "font-semibold text-muted-foreground" : "text-muted-foreground"}><CopyableContent text={provider.text} /></span></div>;
}